import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronDownIcon, MenuIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ContactFormPopup } from './ContactFormPopup';
export function Navigation() {
  const {
    language,
    t,
    getPath,
    navigate
  } = useLanguage();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const handleLanguageChange = (lang: 'en' | 'ru' | 'ua') => {
    const currentPath = window.location.pathname;
    let newPath = currentPath;
    if (currentPath.startsWith('/ru')) {
      newPath = currentPath.substring(3) || '/';
    } else if (currentPath.startsWith('/en')) {
      newPath = currentPath.substring(3) || '/';
    }
    if (lang === 'ru') {
      newPath = `/ru${newPath === '/' ? '' : newPath}`;
    } else if (lang === 'en') {
      newPath = `/en${newPath === '/' ? '' : newPath}`;
    }
    window.history.pushState({}, '', newPath);
    window.dispatchEvent(new Event('popstate'));
    setIsLangMenuOpen(false);
  };
  const navLinks = [{
    name: t('nav.howItWorks'),
    href: '#how-it-works'
  }, {
    name: t('nav.pricing'),
    href: '#services'
  }, {
    name: t('nav.faq'),
    href: '#faq'
  }, {
    name: t('nav.testimonials'),
    href: '#testimonials'
  }];
  const handleInternalLink = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    e.preventDefault();
    navigate(path);
  };
  return <>
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{
      backgroundColor: 'var(--bg-primary)' + 'E6',
      borderBottom: '1px solid var(--border)'
    }} data-id="element-215">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between relative" data-id="element-216">
          {/* Logo */}
          <a href={getPath('')} onClick={e => handleInternalLink(e, '')} className="flex items-center gap-1 z-50" data-id="element-217">
            <span className="text-xl sm:text-2xl font-bold tracking-tight" style={{
            color: 'var(--text-primary)'
          }} data-id="element-218">
              M
            </span>
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full" style={{
            backgroundColor: 'var(--accent)'
          }} data-id="element-219" />
          </a>

          {/* Mobile CTA Button (Centered) */}
          <div className="absolute left-1/2 -translate-x-1/2 md:hidden z-50" data-id="element-220">
            <button onClick={() => setIsPopupOpen(true)} className="px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:scale-105 whitespace-nowrap shadow-sm" style={{
            backgroundColor: 'var(--accent)',
            color: '#FFFFFF'
          }} data-id="element-221">
              {t('nav.getStarted')}
            </button>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-8" data-id="element-222">
            {navLinks.map(link => <a key={link.name} href={link.href} onClick={e => handleInternalLink(e, link.href)} className="text-sm font-semibold transition-colors hover:opacity-70" style={{
            color: 'var(--text-primary)'
          }} data-id="element-223">
                {link.name}
              </a>)}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-224">
            {/* Language Dropdown (Desktop Only) */}
            <div className="hidden md:block relative" data-id="element-225">
              <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)'
            }} data-id="element-226">
                <span className="uppercase" data-id="element-227">{language}</span>
                <ChevronDownIcon className="w-4 h-4" style={{
                color: 'var(--text-muted)'
              }} data-id="element-228" />
              </button>

              <AnimatePresence data-id="element-229">
                {isLangMenuOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsLangMenuOpen(false)} data-id="element-230" />

                    <motion.div initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.2
                }} className="absolute top-full mt-2 right-0 rounded-lg border overflow-hidden shadow-lg min-w-[80px] z-50" style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderColor: 'var(--border)'
                }} data-id="element-231">
                      {['en', 'ru', 'ua'].map(lang => <button key={lang} onClick={() => handleLanguageChange(lang as 'en' | 'ru' | 'ua')} className="w-full px-4 py-2 text-left text-sm font-medium transition-colors" style={{
                    backgroundColor: language === lang ? 'var(--accent)' : 'transparent',
                    color: language === lang ? '#FFFFFF' : 'var(--text-primary)'
                  }} data-id="element-232">
                          {lang.toUpperCase()}
                        </button>)}
                    </motion.div>
                  </>}
              </AnimatePresence>
            </div>

            {/* CTA Button */}
            <button onClick={() => setIsPopupOpen(true)} className="hidden md:block px-6 py-2.5 rounded-lg text-sm font-semibold transition-all hover:scale-105 whitespace-nowrap" style={{
            backgroundColor: 'var(--accent)',
            color: '#FFFFFF'
          }} data-id="element-233">
              {t('nav.getStarted')}
            </button>

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2 z-50" style={{
            color: 'var(--text-primary)'
          }} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-234">
              {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-235" /> : <MenuIcon className="w-6 h-6" data-id="element-236" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence data-id="element-237">
          {isMobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="md:hidden border-b overflow-hidden" style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--border)'
        }} data-id="element-238">
              <div className="px-4 py-6 flex flex-col gap-6" data-id="element-239">
                <div className="flex flex-col gap-4" data-id="element-240">
                  {navLinks.map(link => <a key={link.name} href={link.href} onClick={e => {
                handleInternalLink(e, link.href);
                setIsMobileMenuOpen(false);
              }} className="text-lg font-semibold" style={{
                color: 'var(--text-primary)'
              }} data-id="element-241">
                      {link.name}
                    </a>)}
                </div>

                <div className="h-px w-full" style={{
              backgroundColor: 'var(--border)'
            }} data-id="element-242" />

                <div className="flex flex-col gap-4" data-id="element-243">
                  <span className="text-sm font-medium" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-244">
                    Language
                  </span>
                  <div className="flex gap-2" data-id="element-245">
                    {['en', 'ru', 'ua'].map(lang => <button key={lang} onClick={() => handleLanguageChange(lang as 'en' | 'ru' | 'ua')} className="px-4 py-2 rounded-lg text-sm font-bold uppercase transition-colors" style={{
                  backgroundColor: language === lang ? 'var(--accent)' : 'var(--bg-tertiary)',
                  color: language === lang ? '#FFFFFF' : 'var(--text-primary)'
                }} data-id="element-246">
                        {lang}
                      </button>)}
                  </div>
                </div>

                <button onClick={() => {
              setIsMobileMenuOpen(false);
              setIsPopupOpen(true);
            }} className="w-full py-4 rounded-xl text-base font-semibold transition-all" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-247">
                  {t('nav.getStarted')}
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-248" />
    </>;
}